import {
  HomeworkItem,
  StudentAssignmentListItem,
  StudentAssignmentDetail,
  AssignmentTeacherAttachment,
  AssignmentSubmittedFile,
  StudentProjectListItem,
} from '../types';
import { Platform } from 'react-native';

// Maps the backend's raw Homework model (Subject.Name, Title, DueDate, Status, IsDraft, ...)
// into the app's flattened HomeworkItem shape used across HomeScreen/HomeworkScreen.
export function mapHomework(raw: any): HomeworkItem {
  const dueDate = raw.DueDate ?? raw.dueDate;
  const isOverdue = dueDate ? new Date(dueDate) < new Date(new Date().toDateString()) : false;

  const id = raw.Id ?? raw.id ?? '';
  let fileUrl = raw.FileUrl ?? raw.fileUrl;

  // Only the Homework module serves attachments at this path. A row from the
  // Assessment module carries no FileUrl, so it never reaches this branch -
  // pointing an assessment's id at /Homework/download would 404.
  if (fileUrl) {
    fileUrl = `/Homework/download/image/${id}`;
  }

  // The server stamps Status with the selected child's hand-in state when the
  // list was asked for with an enrollmentId; the due-date guess is only the
  // fallback for anything not handed in.
  const serverStatus = String(raw.Status ?? raw.status ?? '').toLowerCase();
  const status: HomeworkItem['status'] =
    serverStatus === 'graded' ? 'graded'
    : serverStatus === 'submitted' ? 'submitted'
    : isOverdue ? 'overdue' : 'pending';

  return {
    id: raw.Id ?? raw.id ?? '',
    subject: raw.Subject?.Name ?? raw.subjectName ?? raw.SubjectName ?? raw.subject ?? '',
    title: raw.Title ?? raw.title ?? '',
    description: raw.Instructions ?? raw.instructions ?? raw.description ?? '',
    dueDate: dueDate ?? '',
    assignedDate: raw.AssignedDate ?? raw.assignedDate ?? '',
    status,
    teacherName: raw.TeacherName ?? raw.teacherName ?? '',
    attachments: fileUrl ? [fileUrl] : undefined,
    // Present only on rows from the Assessment module - the merged form's rows
    // are shaped like homework on purpose, so this is the one thing that says
    // which kind of work a card is showing.
    category: raw.CategoryName ?? raw.categoryName ?? undefined,
    // Tagged by getHomeworkFeed. Defaults to 'homework' so anything mapped
    // straight off the homework endpoint keeps working unchanged.
    source: raw.__source === 'assessment' ? 'assessment' : 'homework',
    // Whether there is anything to upload.
    //
    // Legacy homework always is. An assessment is unless it is a timed PAPER,
    // which is sat in class - the server refuses a hand-in against one, so
    // showing a file picker there only lets a parent choose a file, press Hand
    // in, and be told no afterwards.
    //
    // "Not a paper" rather than "is a hand-in" on purpose: if the shape is
    // missing from the payload the uploader still appears, because stranding
    // work a family cannot submit is the worse of the two failures.
    collectsUpload: raw.__source === 'assessment'
      ? (raw.deliveryShape ?? raw.DeliveryShape) !== 'Paper'
      : true,
    // What the TEACHER attached. Legacy homework carries a single file on the
    // row itself (mapped into `attachments` above); the assessment module
    // carries a list, each with its own authorised fetch route.
    teacherFiles: (raw.teacherFiles ?? raw.TeacherFiles ?? []).map((f: any) => ({
      id: f.id ?? f.Id,
      fileUrl: f.fileUrl ?? f.FileUrl,
      fileName: f.fileName ?? f.FileName,
    })),
  };
}

// Assignment endpoints are documented as returning camelCase (assignmentId, title, ...);
// the Pascal-case fallbacks below are defensive only, matching this file's existing
// mapHomework convention in case the backend's JSON casing policy ever changes.
export function mapAssignmentListItem(raw: any): StudentAssignmentListItem {
  return {
    assignmentId: raw.assignmentId ?? raw.AssignmentId ?? '',
    title: raw.title ?? raw.Title ?? '',
    assignmentType: raw.assignmentType ?? raw.AssignmentType,
    className: raw.className ?? raw.ClassName ?? '',
    subjectName: raw.subjectName ?? raw.SubjectName ?? '',
    dueDate: raw.dueDate ?? raw.DueDate,
    totalPoints: raw.totalPoints ?? raw.TotalPoints,
    submissionStatus: raw.submissionStatus ?? raw.SubmissionStatus ?? 'NotSubmitted',
    isLate: raw.isLate ?? raw.IsLate ?? false,
    receivedPoints: raw.receivedPoints ?? raw.ReceivedPoints,
    isGradePublished: raw.isGradePublished ?? raw.IsGradePublished ?? false,
  };
}

function mapAssignmentAttachment(raw: any): AssignmentTeacherAttachment {
  return {
    id: raw.id ?? raw.Id ?? '',
    contentLibraryItemId: raw.contentLibraryItemId ?? raw.ContentLibraryItemId ?? '',
    title: raw.title ?? raw.Title ?? '',
    contentType: raw.contentType ?? raw.ContentType ?? '',
    fileUrl: raw.fileUrl ?? raw.FileUrl,
    linkUrl: raw.linkUrl ?? raw.LinkUrl,
    isRequired: raw.isRequired ?? raw.IsRequired ?? false,
    orderIndex: raw.orderIndex ?? raw.OrderIndex ?? 0,
  };
}

function mapAssignmentSubmittedFile(raw: any): AssignmentSubmittedFile {
  return {
    id: raw.id ?? raw.Id ?? '',
    fileUrl: raw.fileUrl ?? raw.FileUrl ?? '',
    fileName: raw.fileName ?? raw.FileName ?? '',
  };
}

export function mapAssignmentDetail(raw: any): StudentAssignmentDetail {
  return {
    assignmentId: raw.assignmentId ?? raw.AssignmentId ?? '',
    title: raw.title ?? raw.Title ?? '',
    assignmentType: raw.assignmentType ?? raw.AssignmentType,
    instructions: raw.instructions ?? raw.Instructions,
    className: raw.className ?? raw.ClassName ?? '',
    subjectName: raw.subjectName ?? raw.SubjectName ?? '',
    dueDate: raw.dueDate ?? raw.DueDate,
    allowLateSubmission: raw.allowLateSubmission ?? raw.AllowLateSubmission ?? false,
    lockAfterDueDate: raw.lockAfterDueDate ?? raw.LockAfterDueDate ?? false,
    submissionTypeFile: raw.submissionTypeFile ?? raw.SubmissionTypeFile ?? false,
    submissionTypeText: raw.submissionTypeText ?? raw.SubmissionTypeText ?? false,
    allowedFileFormats: raw.allowedFileFormats ?? raw.AllowedFileFormats,
    maxFileCount: raw.maxFileCount ?? raw.MaxFileCount,
    attemptsAllowed: raw.attemptsAllowed ?? raw.AttemptsAllowed ?? 1,
    totalPoints: raw.totalPoints ?? raw.TotalPoints,
    attachments: (raw.attachments ?? raw.Attachments ?? []).map(mapAssignmentAttachment),
    submissionId: raw.submissionId ?? raw.SubmissionId ?? '',
    submissionStatus: raw.submissionStatus ?? raw.SubmissionStatus ?? 'NotSubmitted',
    attemptNumber: raw.attemptNumber ?? raw.AttemptNumber ?? 0,
    submissionText: raw.submissionText ?? raw.SubmissionText,
    submittedFiles: (raw.submittedFiles ?? raw.SubmittedFiles ?? []).map(mapAssignmentSubmittedFile),
    isLate: raw.isLate ?? raw.IsLate ?? false,
    submittedDate: raw.submittedDate ?? raw.SubmittedDate,
    receivedPoints: raw.receivedPoints ?? raw.ReceivedPoints,
    feedbackText: raw.feedbackText ?? raw.FeedbackText,
    isGradePublished: raw.isGradePublished ?? raw.IsGradePublished ?? false,
  };
}

// Projects reuse the single `/Project/parent/class/{classId}` list DTO for both the list
// AND the detail/submission screen — MyStatus/MyGrade/MyTeacherComments only populate once
// studentId is passed to that endpoint, and are null until the teacher publishes grades.
export function mapProjectListItem(raw: any): StudentProjectListItem {
  return {
    id: raw.id ?? raw.Id ?? '',
    title: raw.title ?? raw.Title ?? '',
    description: raw.description ?? raw.Description,
    learningObjectives: raw.learningObjectives ?? raw.LearningObjectives,
    materialsRequired: raw.materialsRequired ?? raw.MaterialsRequired,
    subjectName: raw.subjectName ?? raw.SubjectName,
    startDate: raw.startDate ?? raw.StartDate,
    dueDate: raw.dueDate ?? raw.DueDate ?? '',
    totalMarks: raw.totalMarks ?? raw.TotalMarks,
    submissionType: raw.submissionType ?? raw.SubmissionType,
    isGroupProject: raw.isGroupProject ?? raw.IsGroupProject ?? false,
    groupSize: raw.groupSize ?? raw.GroupSize,
    allowLateSubmission: raw.allowLateSubmission ?? raw.AllowLateSubmission ?? false,
    lateSubmissionDueDate: raw.lateSubmissionDueDate ?? raw.LateSubmissionDueDate,
    attachmentUrl: raw.attachmentUrl ?? raw.AttachmentUrl,
    myStatus: raw.myStatus ?? raw.MyStatus ?? 'NotSubmitted',
    myReceivedMarks: raw.myReceivedMarks ?? raw.MyReceivedMarks,
    myGrade: raw.myGrade ?? raw.MyGrade,
    myTeacherComments: raw.myTeacherComments ?? raw.MyTeacherComments,
    mySubmissionText: raw.mySubmissionText ?? raw.MySubmissionText,
    myFileUrl: raw.myFileUrl ?? raw.MyFileUrl,
    mySubmissionId: raw.mySubmissionId ?? raw.MySubmissionId,
    mySubmittedDate: raw.mySubmittedDate ?? raw.MySubmittedDate,
  };
}
